import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HOUSE } from "@/constants/testIds";

const CLOSED_IMG = "/media/abike-closed.jpg";
const DEPLOYED_IMG = "/media/abike-deployed.jpg";

/**
 * Cinematic full-screen hero.
 * Both images always live in the DOM (absolute-stacked) so they preload
 * together. State toggles which one is visible via opacity — the swap
 * is instant and reliable regardless of network speed.
 */
export const Hero = () => {
  const [state, setState] = useState("closed"); // "closed" -> "deployed"

  useEffect(() => {
    // Preload both images explicitly for reliability
    [CLOSED_IMG, DEPLOYED_IMG].forEach((src) => {
      const i = new Image();
      i.src = src;
    });
    const t = setTimeout(() => setState("deployed"), 2200);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      className="relative h-[100svh] min-h-[720px] w-full overflow-hidden bg-[var(--black)]"
    >
      {/* Both images always mounted — cross-fade via opacity */}
      <motion.img
        src={CLOSED_IMG}
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, scale: 1.06 }}
        animate={{
          opacity: state === "closed" ? 1 : 0,
          scale: state === "closed" ? 1.02 : 1.02,
        }}
        transition={{
          opacity: { duration: state === "closed" ? 1.4 : 1.6, ease: [0.22, 1, 0.36, 1] },
          scale: { duration: 3.2, ease: [0.22, 1, 0.36, 1] },
        }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <motion.img
        src={DEPLOYED_IMG}
        alt=""
        aria-hidden="true"
        initial={{ opacity: 0, scale: 1.06 }}
        animate={{
          opacity: state === "deployed" ? 1 : 0,
          scale: state === "deployed" ? 1.0 : 1.06,
        }}
        transition={{
          opacity: { duration: 1.8, ease: [0.22, 1, 0.36, 1] },
          scale: { duration: 2.4, ease: [0.22, 1, 0.36, 1] },
        }}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Cinematic gradient wash for legibility */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[var(--black)]/85 via-[var(--black)]/45 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[var(--black)]/70 via-transparent to-transparent"
      />

      {/* Content */}
      <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.55, ease: "easeOut" }}
          className="font-serif text-[var(--stone)] italic text-lg sm:text-xl md:text-2xl mb-5"
        >
          Introducing the Abike Collection
        </motion.p>

        <motion.h1
          data-testid={HOUSE.heroHeading}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif text-[var(--ivory)] text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight max-w-4xl font-light"
        >
          Everything deserves
          <br />
          <span className="italic text-[var(--gold-light)]">a place.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: "easeOut" }}
          className="mt-8 max-w-xl font-sans text-[var(--stone)] text-base md:text-lg leading-relaxed"
        >
          An architectural platform designed to create an intentional place for
          the things people carry — wherever life takes them.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.15, ease: "easeOut" }}
          className="mt-12 flex flex-wrap items-center gap-4 sm:gap-6"
        >
          <button
            data-testid={HOUSE.heroPrimaryCta}
            onClick={() => scrollTo("solution")}
            className="group inline-flex items-center gap-3 bg-[var(--ivory)] text-[var(--black)] font-sans uppercase tracking-[0.28em] text-[11px] px-8 py-4 hover:bg-[var(--gold-light)] hover:text-[var(--black)] transition-all duration-500"
          >
            Discover Abike
            <span className="w-6 h-px bg-[var(--black)] transition-all duration-500 group-hover:w-10" />
          </button>
          <button
            data-testid={HOUSE.heroSecondaryCta}
            onClick={() => scrollTo("environments")}
            className="group inline-flex items-center gap-3 border border-[var(--stone)]/50 text-[var(--ivory)] font-sans uppercase tracking-[0.28em] text-[11px] px-8 py-4 hover:border-[var(--ivory)] hover:bg-[var(--ivory)]/5 transition-all duration-500"
          >
            Explore the Possibilities
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.6 }}
          className="mt-10 flex items-center gap-4"
        >
          <span className="h-px w-8 bg-[var(--gold-light)]" />
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-[var(--muted-dark)]">
            Set It Down · Patent Pending
          </span>
        </motion.div>
      </div>
    </section>
  );
};
